import { landmineNames } from '@mm/constants/landmine-names';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { FormGroup, Label } from 'reactstrap';
import guardModelMapper from '../../mappers/guardModelMapper';
import { useCatchAllConfirmModal } from '../../use-catch-all-confirm-modal';
import './EditFBPost.scss';
import KeywordsForm from './KeywordsForm/KeywordsForm';
import SpecifiedPostContainer from './SpecifiedPostContainer/SpecifiedPostContainer';
import { useCreateCommentGuard } from '../../../common/use-create-start-bot-promoter-by-type';

type Props = {
  botId: number;
  guard: PromoterModel;
};

export const EditFBPost = ({ guard, botId }: Props) => {
  const modalActions = useModalActions();
  const canEditCommentGuardFbPost = useSelector(
    combinedFeatureFlagsSelector,
  ).commentGuardsEditFbPost;
  const openCatchAllConfirmModal = useCatchAllConfirmModal();
  const remoteCatchAll = useSelector(
    (state: StoreState) => state.promoter.catchAll,
  );

  const promoterCRUDActions = usePromoterCRUDActions();
  const getCatchAll = (botId: number) =>
    promoterCRUDActions.retrieveCatchAllGuard(botId);
  const clearCatchAll = () => promoterCRUDActions.clearCatchAllGuard();
  const setCatchAll = () => {
    const nextGuard = { ...guard, postId: 'ALL' };
    promoterCRUDActions.update(botId, guardModelMapper(nextGuard, botId));
  };

  const commentGuardValidator = useCreateCommentGuard();

  const [showPostContainer, setShowPostContainer] = useState(
    guard.postId !== 'ALL',
  );
  const setShowDropdown = (value: boolean) => setShowPostContainer(value);

  const [showModal, setShowModal] = useState(false);

  const { november2021Pricing } = useSelector(combinedFeatureFlagsSelector);

  const onCatchAllSelect = () => {
    getCatchAll(botId);
  };

  useEffect(() => {
    if (showModal) {
      const onClose = (ok: boolean) => {
        if (ok) {
          setCatchAll();
        }

        setShowModal(false);
      };

      openCatchAllConfirmModal(onClose);
    }
  }, [showModal]);

  useEffect(() => {
    if (remoteCatchAll) {
      if (remoteCatchAll.postId === 'ALL' && remoteCatchAll.id !== guard.id) {
        // We need to this to remove the result of the network call from the state. Otherwise this
        // routine will be executed next time the state is updated.
        clearCatchAll();
        setShowModal(true);
      } else {
        clearCatchAll();
        setCatchAll();
      }
    }
  }, [remoteCatchAll]);

  return (
    <FormGroup className="edit-comment-guard-panel__input-container">
      <Label for="page" className="edit-comment-guard-panel__title">
        Facebook Post
      </Label>
      <FormGroup>
        <div className="editfbpost">
          <div className="editfbpost__radio-text editfbpost__catch-all">
            <input
              className={
                !showPostContainer
                  ? 'editfbpost__radio-input--checked'
                  : 'editfbpost__radio-input'
              }
              type="radio"
              checked={!showPostContainer}
              onChange={() => {
                onCatchAllSelect();
                setShowDropdown(false);
              }}
            />
            Catch all: respond to all unguarded posts
          </div>
          <div
            className={
              canEditCommentGuardFbPost
                ? 'editfbpost__radio-text'
                : 'editfbpost__radio-text--upgrade'
            }
          >
            <input
              className={
                showPostContainer
                  ? 'editfbpost__radio-input--checked'
                  : 'editfbpost__radio-input'
              }
              type="radio"
              disabled={!canEditCommentGuardFbPost}
              checked={showPostContainer}
              onChange={() => setShowDropdown(true)}
            />
            Respond only to specified posts
            {!canEditCommentGuardFbPost && (
              <div className="edit-comment-guard-panel__upgrade-link-container">
                <span
                  onClick={() =>
                    modalActions.openUniversalUpgradeLandmineModal({
                      landmineName:
                        landmineNames[
                          'STARTBOTS-PROMOTERS__COMMENT-GUARDS__EDIT-FB-POST'
                        ],
                      feature: 'commentGuardsEditFbPost',
                    })
                  }
                  className="edit-comment-guard-panel__upgrade-link-container__link"
                >
                  Upgrade to {november2021Pricing ? 'Startup' : 'Operator'}!
                </span>
              </div>
            )}
          </div>
        </div>
      </FormGroup>
      {showPostContainer && (
        <SpecifiedPostContainer guard={guard} botId={botId} />
      )}
      <KeywordsForm
        guard={guard}
        validate={async (promoter, onSuccess) => {
          const valid = commentGuardValidator.validatePromoter(promoter);
          if (!valid) {
            return modalActions.openPrettyErrorModal({
              title: 'Error',
              errorMessage: `You already have an active trigger of that kind`,
            });
          }

          onSuccess();
        }}
      />
    </FormGroup>
  );
};
